
import { defineComponent, ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue';
import * as echarts from 'echarts';
import { useMonitoringStore } from '@/store/monitoring';
import { useUsersStore } from '../../store/users'
import moment from 'moment';
import lodash from 'lodash';

export default defineComponent({
  setup() {
    const monitoringStore = useMonitoringStore();
    const userStore = useUsersStore();
    const loginUser = computed(() => userStore.getLoginUser);
    const patients = computed(() => monitoringStore.getFilteredPatients);
    const currPatient = computed(() => monitoringStore.getCurrPatient);
    const currTab = computed(() => monitoringStore.getCurrTab);
    const selectedPuOption = computed(() => monitoringStore.getSelectedPuOption);
    const puThreshold = computed(() => monitoringStore.getPuThreshold);
    const trackThreshold = computed(() => monitoringStore.getTrackThreshold);
    const inputPuThreshold = ref(lodash.cloneDeep(puThreshold.value));
    const inputTrackThreshold = ref(lodash.cloneDeep(trackThreshold.value));

    const updatePuThreshold = async (): Promise<void> => {
      await monitoringStore.updatePuThreshold(inputPuThreshold.value);
      processPatientData();
    }

    const resetPuThreshold = async (): Promise<void> => {
      await monitoringStore.resetPuThreshold();
      processPatientData();
    }

    const updateTrackThreshold = async (): Promise<void> => {
      await monitoringStore.updateTrackThreshold(inputTrackThreshold.value);
      processPatientData();
    }

    const resetTrackThreshold = async (): Promise<void> => {
      await monitoringStore.resetTrackThreshold();
      processPatientData();
    }

    const setCurrTab = (tab: string) => {
      monitoringStore.setCurrTab(tab);
    }

    const setPuOption = (option: string) => {
      monitoringStore.setPuOption(option);
    }

    let myChart: echarts.ECharts | null = null;

    const xAxisData = ref<string[]>([]);
    const yAxisData = ref<(number | { value: number;  itemStyle: { color: string; borderColor: string; borderWidth: number }; label: {color: string } })[]>([]);
    
    let dataZoomStart = 0;
    let dataZoomEnd = 0;

    const drawChart = () => {
      const chartDom = document.getElementById('chart');
      
      if (!chartDom) return console.warn('Chart DOM not found.');
      if (myChart) myChart.dispose();
      if (xAxisData.value.length === 0 || yAxisData.value.length === 0) {
        return console.warn('No data available for chart');
      }
      
      myChart = echarts.init(chartDom);
      
      const tab = currTab.value;
      const puOption = selectedPuOption.value;
      const patient = currPatient.value;

      const visibleDataCount = 10;
      const totalDataCount = xAxisData.value.length;

      if (totalDataCount === 0) {
        return console.warn('No data to display on the chart.');
      }

      dataZoomStart = 100 - ((visibleDataCount - 1) / totalDataCount) * 100;
      dataZoomEnd = 100;

      const commonOptions = {
        grid: {
          width: '95.5%',
          left: '3%',
          top: '5%',
        },
        xAxis: {
          type: 'category',
          data: xAxisData.value,
          scale: true,
          boundaryGap: true,
          axisLine: { lineStyle: { color: '#fff' } },
          axisLabel: { fontSize: 14, color: '#ffffff' },
          axisTick: { alignWithLabel: true },
        },
        yAxis: {
          type: 'value',
          scale: true,
          max: 100,
          axisLine: { lineStyle: { color: '#fff' } },
          axisLabel: { fontSize: 14, color: '#ffffff' },
          splitLine: { lineStyle: { color: '#46495E' } },
        },
        dataZoom: [
          {
            type: 'slider',
            start: dataZoomStart,
            end: dataZoomEnd,
            zoomLock: true,
            brushSelect: false,
          },
        ],
      };

      let series: any[] = [];
      let tooltip: any = {};

      interface CandleDatum {
        value: [number, number, number, number, number]; // [open, close, min, max, avg]
        thresholdExceeded: boolean;
        itemStyle: {
          color: string;
          borderColor: string;
        };
      }

      if (tab === 'pu' && puOption === '1d') {
        const candleData: CandleDatum[] = patient.DAILY_PU_INFO.map((info: any) => {
          const open = Number((info.min_value * 100).toFixed(0));
          const close = Number((info.max_value * 100).toFixed(0));
          const lowest = Number((info.min_value * 100).toFixed(0));
          const highest = Number((info.max_value * 100).toFixed(0));
          const avg = Number((info.avg_value * 100).toFixed(0));
          const thresholdExceeded = highest >= puThreshold.value;

          return {
            value: [open, close, lowest, highest, avg],
            thresholdExceeded,
            itemStyle: {
              color: thresholdExceeded ? '#FF4955' : '#6DCC65',
              borderColor: thresholdExceeded ? 'transparent' : 'transparent',
            },
          };
        });

        tooltip = {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: 'transparent',
              color: '#transparent',
              borderColor: 'transparent',
            },
          },
          formatter: function (params: any[]) {
            let tooltipHtml = '';

            const candle = params.find((p) => p.seriesType === 'candlestick');
            if (candle) {
              const data = candle.value;
              tooltipHtml += `
                <div>
                   ${candle.marker}<strong>lowest:</strong> ${data[3]}<br/>
                   ${candle.marker}<strong>highest:</strong> ${data[4]}<br/>
                   ${candle.marker}<strong>average:</strong> ${data[5]}<br/>
                </div>
              `;
            }
            return tooltipHtml;
          }
        };

        series = [{
          type: 'candlestick',
          data: candleData,
          barWidth: 25,
          markPoint: {
            symbol: 'rect',
            symbolSize: [40, 1],
            label: { show: false },
            emphasis: {
              scale: false,
              itemStyle: {
                borderWidth: 0,
                color: null,
              },
              label: { show: false },
            },
            data: candleData.map((item: CandleDatum, index: number) => {
              const middle = item.value;
              console.log('middle >> ', middle, middle.length-1);

              return {
                coord: [xAxisData.value[index], middle[middle.length-1]],
                itemStyle: {
                  color: item.thresholdExceeded ? '#FF4955' : '#6DCC65',
                },
              };
            }),
          },
        }];

      } else {
        // 라인 차트
        tooltip = {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: 'transparent',
              color: '#transparent',
              borderColor: 'transparent',
            },
          },
        };

        series = [{
          type: 'line',
          data: yAxisData.value,
          symbolSize: 8,
          lineStyle: {
            color: '#68ADE6',
            width: 3,
          },
          label: {
            show: true,
            fontSize: 15,
            fontWeight: 'bold',
            color: '#68ADE6',
          },
        }];
      }

      myChart.setOption({ ...commonOptions, tooltip, series });

      // 데이터 줌 이벤트
      myChart.on('dataZoom', (event: any) => {
        const totalPoints = xAxisData.value.length;
        let startIndex = Math.round((event.start / 100) * (totalPoints - 1));
        let endIndex = Math.round((event.end / 100) * (totalPoints - 1));

        if (endIndex - startIndex + 1 > visibleDataCount) {
          endIndex = startIndex + visibleDataCount - 1;
        }

        if (endIndex - startIndex + 1 < visibleDataCount) {
          startIndex = Math.max(0, endIndex - visibleDataCount + 1);
        }

        let selectedData;

        if (tab === 'pu' && puOption === '1h') {
          selectedData = patient?.PU_INFO.slice(startIndex, endIndex + 1).map((item: any) => ({
            PU_DATE: moment(item.PU_DATE).format('YYYY.MM.DD HH:mm'),
            RESP_RATE: item.RESP_RATE,
            SEDATION: item.SEDATION,
            DIARRHEA: item.DIARRHEA,
            RESTRAINT: item.RESTRAINT,
            REPOSITION: item.REPOSITION,
            HCO3: item.HCO3,
          }));
          monitoringStore.setChartData(selectedData);
        } else if (tab === 'pu' && puOption === '1d') {
          selectedData = patient?.DAILY_PU_INFO.slice(startIndex, endIndex + 1).map((item: any) => ({
            PU_DATE: moment(item.daily_pu_date).format('YYYY.MM.DD'),
            RESP_RATE: item.avg_resp_rate,
            SEDATION: item.sedation,
            DIARRHEA: item.diarrhea,
            RESTRAINT: item.restraint,
            REPOSITION: item.reposition,
            HCO3: item.avg_hco3,
          }));
          monitoringStore.setChartData(selectedData);
        } else if (tab === 'track') {
          selectedData = patient?.TRACK_INFO.slice(startIndex, endIndex + 1).map((item: any) => ({
            track_date: moment(item.track_date).format('YYYY.MM.DD'),
            heart_rate: item.heart_rate,
            resp_rate: item.resp_rate,
            spo2: item.spo2,
            urineoutput_24hr: item.urineoutput_24hr,
            gcs: item.gcs,
          }));
          monitoringStore.setTrackChartData(selectedData);
        }
      });
    };

    const processPatientData = () => {
      const patient = currPatient.value;
      const tab = currTab.value;
      const puOption = selectedPuOption.value;

      // PU: 1시간 단위
      if (tab === 'pu' && puOption === '1h') {
        if (Array.isArray(patient?.PU_INFO)) {
          xAxisData.value = patient.PU_INFO.map((info: any) =>
            moment(info.PU_DATE).format('YYYY.MM.DD HH:mm')
          );

          yAxisData.value = patient.PU_INFO.map((info: any) => {
            const predictionValue = Number((info.PREDICTION * 100).toFixed(0));
            const exceedsThreshold = predictionValue >= puThreshold.value;

            return exceedsThreshold
              ? {
                  value: predictionValue,
                  itemStyle: {
                    color: '#FF4450',
                    borderColor: '#FF4450',
                    borderWidth: 10,
                  },
                  label: { color: '#FF4450' },
                }
              : predictionValue;
          });
        }
      }
      // PU: 하루 단위 - yAxisData는 사용하지 않음
      else if (tab === 'pu' && puOption === '1d') {
        if (Array.isArray(patient?.DAILY_PU_INFO)) {
          xAxisData.value = patient.DAILY_PU_INFO.map((info: any) =>
            moment(info.daily_pu_date).format('YYYY.MM.DD')
          );

          yAxisData.value = patient.PU_INFO.map((info: any) => {});
        }
      }
      // 퇴실 후 악화
      else if (tab === 'track') {
        if (Array.isArray(patient?.TRACK_INFO)) {
          xAxisData.value = patient.TRACK_INFO.map((info: any) =>
            moment(info.track_date).format('YYYY.MM.DD')
          );

          yAxisData.value = patient.TRACK_INFO.map((info: any) => {
            const predictionValue = Number((info.prediction * 100).toFixed(0));
            const exceedsThreshold = predictionValue >= trackThreshold.value;

            return exceedsThreshold
              ? {
                  value: predictionValue,
                  itemStyle: {
                    color: '#FF4955',
                    borderColor: '#FF4955',
                    borderWidth: 10,
                  },
                  label: { color: '#FF4955' },
                }
              : predictionValue;
          });
        }
      }
      drawChart();
    };

    const initChart = () => {
      processPatientData();
      if (myChart) {
        myChart.dispatchAction({
          type: 'dataZoom',
          start: dataZoomStart,
          end: dataZoomEnd,
        });
      }
    }

    watch(() => currPatient.value?.PU_INFO, (newVal, oldVal) => {
      if (newVal !== oldVal && newVal != undefined) {
        initChart();
      }
    });

    watch(() => currTab.value, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        initChart();
      }
    });
    
    watch(() => selectedPuOption.value, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        initChart();
      }
    });

    watch(() => puThreshold.value, (newVal) => {
      inputPuThreshold.value = lodash.cloneDeep(puThreshold.value);
    });

    watch(() => trackThreshold.value, (newVal) => {
      inputTrackThreshold.value = lodash.cloneDeep(trackThreshold.value);
    });

    const handleResize = () => {
      if (myChart) myChart.resize();
    };

    onMounted(async () => {
      await nextTick();
      await monitoringStore.readPuThreshold();
      await monitoringStore.readTrackThreshold();
      initChart();
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      monitoringStore.setPuOption('1h');
      if (myChart) myChart.dispose();
      window.removeEventListener('resize', handleResize);
    });

    return {
      currTab,
      currPatient,
      selectedPuOption,
      setCurrTab,
      setPuOption,
      puThreshold,
      trackThreshold,
      updatePuThreshold,
      updateTrackThreshold,
      inputPuThreshold,
      inputTrackThreshold,
      resetPuThreshold,
      resetTrackThreshold,
      loginUser
    };
  },
});

